import { useEffect, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { ArrowUpCircleOutline, Minus, Plus } from 'mdi-material-ui';
import { allowedRanges, getColour, getRotationAngle, getText } from './helpers';
import useCostComparisonQueries from './helpers/useCostComparisonQueries';

interface CostComparisonProps {
  selectedRange: string;
  orderByValue: string;
  initialAverage: string;
}

const rangeMap = new Map(allowedRanges.map(range => [range[0], range[1]]));

const Trend = ({ value, percentage, range }: { value: number; percentage: number; range: string }) => {
  return (
    <Stack direction="row" gap="10px" alignItems="center">
      {value === 0 ? null : (
        <ArrowUpCircleOutline htmlColor={getColour(value)} sx={{ transform: `rotate(${getRotationAngle(value)})` }} />
      )}

      <Typography
        sx={{ fontSize: '1.571rem', color: '#acacac', textTransform: 'capitalize' }}
        dangerouslySetInnerHTML={{ __html: getText(value, percentage, range) }}
      />
    </Stack>
  );
};

const Difference = ({ value }: { value: number }) => {
  const numberFormatter = new Intl.NumberFormat();

  if (value === 0) {
    return null;
  }

  return (
    <Stack direction="row" gap="10px" alignItems="center">
      {value > 0 ? <Plus htmlColor={getColour(value)} /> : <Minus htmlColor={getColour(value)} />}
      <Typography sx={{ fontSize: '1.571rem', color: '#acacac', textTransform: 'uppercase' }}>
        {`£${numberFormatter.format(Math.abs(value))}`}
      </Typography>
    </Stack>
  );
};

const CostComparison = ({ selectedRange, orderByValue, initialAverage = '0.00' }: CostComparisonProps) => {
  const [requiredRange, setRequiredRange] = useState<string | undefined>(undefined);
  const [differenceValue, setDifferenceValue] = useState<number>(0);
  const [percentageChange, setPercentageChange] = useState<number>(0);

  useCostComparisonQueries({ orderByValue, requiredRange, initialAverage, setDifferenceValue, setPercentageChange });

  useEffect(() => {
    const x = rangeMap.get(selectedRange);

    setRequiredRange(x);
  }, [selectedRange]);

  if (!requiredRange) {
    return null;
  }

  return (
    <Stack direction="column">
      <Trend value={differenceValue} percentage={percentageChange} range={requiredRange ?? ''} />
      <Difference value={differenceValue} />
    </Stack>
  );
};

export default CostComparison;
